// Team-wide main colour → full palette (tints, hover, text shade, warm/cool greys).
// Works in OKLCH so generated shades look even; checks readability and closeness to status colours.

export const DEFAULT_ACCENT = '#2457c5';

export const PRESETS = [
  ['Ink blue', '#2457c5'],
  ['Mulberry', '#8c3354'],
  ['Graphite', '#2e3035'],
  ['Indigo', '#3b4fb8'],
  ['Deep teal', '#0e6f78'],
  ['Saffron', '#c97a12'],
  ['Forest', '#2f7a4f'],
];

const toLin = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGam = (c) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

function hexToRgb(h) {
  let x = h.replace('#', '');
  if (x.length === 3) x = x.split('').map((c) => c + c).join('');
  const n = parseInt(x, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => v / 255);
}

const rgbToHex = (rgb) => `#${rgb.map((v) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0')).join('')}`;

function rgbToOklch(rgb) {
  const [r, g, b] = rgb.map(toLin);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return [L, Math.hypot(A, B), ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360];
}

function oklchToRgbRaw(L, C, H) {
  const a = C * Math.cos((H * Math.PI) / 180);
  const b = C * Math.sin((H * Math.PI) / 180);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ].map(toGam);
}

// Reduce chroma until the colour fits in sRGB
function oklch(L, C, H) {
  let c = C;
  let rgb = oklchToRgbRaw(L, c, H);
  while (c > 0 && rgb.some((v) => v < -0.001 || v > 1.001)) {
    c -= 0.004;
    rgb = oklchToRgbRaw(L, Math.max(c, 0), H);
  }
  return rgbToHex(rgb);
}

const luminance = (hex) => hexToRgb(hex).map(toLin).reduce((acc, v, i) => acc + v * [0.2126, 0.7152, 0.0722][i], 0);

export function contrast(a, b) {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
}

const hueGap = (a, b) => {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
};

export const isHex = (v) => /^#[0-9a-f]{6}$/i.test(String(v || ''));

export function buildTheme(hex) {
  const safe = isHex(hex) ? hex.toLowerCase() : DEFAULT_ACCENT;
  let [L, C, H] = rgbToOklch(hexToRgb(safe));
  const notes = [];
  let base = safe;
  let guard = 0;
  while (contrast(base, '#ffffff') < 4.5 && guard < 40) {
    L -= 0.015;
    base = oklch(L, C, H);
    guard += 1;
  }
  if (guard > 0) notes.push(['warn', 'Darkened slightly so white button text and links stay readable.']);
  if (C > 0.07) {
    [[27, 'urgent red'], [70, 'waiting amber'], [148, 'resolved green']].forEach(([h, name]) => {
      if (hueGap(H, h) < 16) notes.push(['warn', `This is close to the ${name} used for status. Status colours stay fixed, so pick another hue if things start to look alike.`]);
    });
  }
  if (!notes.length) notes.push(['ok', 'Readable on every surface and clearly different from status colours.']);
  const nC = Math.min(0.012, C * 0.12 + 0.002);
  const vars = {
    '--acc': base,
    '--acc-hover': oklch(L - 0.06, C, H),
    '--acc-50': oklch(0.975, Math.min(C * 0.22, 0.02), H),
    '--acc-100': oklch(0.935, Math.min(C * 0.35, 0.045), H),
    '--acc-200': oklch(0.86, Math.min(C * 0.5, 0.08), H),
    '--acc-700': oklch(Math.min(L - 0.08, 0.42), Math.min(C, 0.16), H),
    '--on-acc': '#ffffff',
    '--bg': oklch(0.98, nC * 0.6, H),
    '--panel': oklch(0.988, nC * 0.4, H),
    '--surface': '#ffffff',
    '--sunk': oklch(0.958, nC, H),
    '--line': oklch(0.918, nC, H),
    '--line-2': oklch(0.862, nC * 1.2, H),
    '--ink': oklch(0.225, nC * 1.6, H),
    '--ink-2': oklch(0.45, nC * 1.6, H),
    '--ink-3': oklch(0.585, nC * 1.4, H),
  };
  return { vars, notes, base };
}

export function themeStyle(hex) {
  return buildTheme(hex).vars;
}
